import React, { FC } from 'react';
import { Document, Font, StyleSheet } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { IGetIndividualReport200Resp } from '../../../services/individualReport';
import { THEME as theme } from '../../../styles/theme';
import robotoBold from './../../../assets/fonts/Roboto-Bold.ttf';
import robotoRegular from './../../../assets/fonts/Roboto-Regular.ttf';
import CoverPage from './CoverPage/CoverPage';
import IntroductionPage from './IntroductionPage/IntroductionPage';
import KeyTakeawaysPage from './KeyTakeawaysPage/KeyTakeawaysPage';
import PerceivedWorkEnvironmentPage from './PerceivedWorkEnvironmentPage/PerceivedWorkEnvironmentPage';
import PredictiveStatesPage from './PredictiveStatesPage/PredictiveStatesPage';
import ReflectionTaskPage from './ReflectionTaskPage/ReflectionTaskPage';
import SuggestionsPage from './SuggestionsPage/SuggestionsPage';

Font.register({ family: 'Roboto-Regular', src: robotoRegular });
Font.register({ family: 'Roboto-Bold', src: robotoBold });

Font.registerHyphenationCallback(word => [word]);

const styles = StyleSheet.create({
  document: {
    fontFamily: 'Roboto-Regular',
    fontSize: 10,
    lineHeight: 1.15,
    color: theme.colors.neutral.primary[100],
  },
});

interface IIndividualReportPDF {
  response: IGetIndividualReport200Resp;
  language: string;
}

const IndividualReportPDF: FC<IIndividualReportPDF> = ({ response, language }) => {
  const { t } = useTranslation();

  const { data, respondent, survey } = response;

  const {
    introduction,
    individual_states: individualStates,
    key_takeaways: keyTakeaways,
    work_environment: workEnvironment,
    prm_code: prmCode,
  } = data ?? {};

  return (
    <Document
      author="Communiq"
      creator="Communiq"
      subject={t('results.report.pdf.subject', { data: { respondent } })}
      title={`Communiq ${respondent}`}
      style={styles.document}
      {...{ language }}
    >
      <CoverPage {...{ respondent, survey }} />
      {introduction ? <IntroductionPage {...{ introduction }} /> : null}
      {workEnvironment ? (
        <PerceivedWorkEnvironmentPage mode="results" {...{ workEnvironment }} />
      ) : null}
      {workEnvironment ? (
        <PerceivedWorkEnvironmentPage mode="group" {...{ workEnvironment }} />
      ) : null}
      {individualStates ? <PredictiveStatesPage mode="results" {...{ individualStates }} /> : null}
      {individualStates ? <PredictiveStatesPage mode="group" {...{ individualStates }} /> : null}
      {keyTakeaways ? <KeyTakeawaysPage {...{ keyTakeaways }} /> : null}
      <ReflectionTaskPage />
      {prmCode ? <SuggestionsPage {...{ prmCode }} /> : null}
    </Document>
  );
};

export default IndividualReportPDF;
